import React, { useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import FilterSelect from './FilterSelect';
import KpiTile from './KpiTile';
import PivotTable, { type PivotColumn, type PivotRow } from './PivotTable';
import {
  BOARD_SELECTS,
  BOARD_VIEWS,
  type BoardFilters,
  type BoardView,
  DAILY_BOARD_PAGE_ID,
  PRODUCT_CATEGORIES,
  SALES_PERIODS,
  YEAR_TYPES,
  boardRequest,
  parseBoardFilters,
  ratesOf,
  resetBoardFilters,
  sequentialGrowth,
  totalsOf,
  writeBoardFilters,
} from './boardModel';
import { type Crosstab, type Grain, bucketLabel, crosstab, shareOfColumn } from './boardTables';
import { DATE_KEY, type ValueFormat, growth, isProblem, num, ratio, readCard, text } from './cards';
import { type PageSection, boardEditFromBridge, dailyBoardPageView } from './pageAdapter';

const LENS_ID = nativeLensId(DAILY_BOARD_PAGE_ID);
const SOURCE = 'lux-analytics-prod.analytics.sales_with_refunds';

const SUM_COLUMNS: Array<PivotColumn> = [
  { key: 'gross_sales_aud', label: 'Gross sales', format: 'currency' },
  { key: 'refunds_aud', label: 'Refunds', format: 'currency' },
  { key: 'net_sales_aud', label: 'Net sales', format: 'currency' },
  { key: 'net_margin_aud', label: 'Gross margin (AR)', format: 'currency' },
];
const PAY_COLUMNS: Array<PivotColumn> = [
  { key: 'cash_sales_aud', label: 'Cash', format: 'currency' },
  { key: 'credit_sales_aud', label: 'Credit', format: 'currency' },
  { key: 'points_sales_aud', label: 'Points', format: 'currency' },
  { key: 'other_sales_aud', label: 'Promo, voucher, airline pts', format: 'currency' },
];
const ATTACH = [
  { value: 'orders_with_flight', label: 'Flight' },
  { value: 'orders_with_insurance', label: 'Insurance' },
  { value: 'orders_with_experience', label: 'Experience' },
] as const;

const rowsOf = (
  rows: ReadonlyArray<Record<string, unknown>>,
  label: (row: Record<string, unknown>) => string,
  columns: Array<PivotColumn>,
): Array<PivotRow> =>
  rows.map((row) => ({
    label: label(row),
    values: Object.fromEntries(columns.map((column) => [column.key, num(row[column.key])])),
  }));

const totalOf = (rows: Array<PivotRow>, columns: Array<PivotColumn>): PivotRow => ({
  label: 'Total',
  values: Object.fromEntries(
    columns.map((column) => {
      const known = rows.map((row) => row.values[column.key]).filter((value): value is number => value !== null);
      return [column.key, known.length ? known.reduce((sum, value) => sum + value, 0) : null];
    }),
  ),
});

function DailyBoardContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [attach, setAttach] = useState<(typeof ATTACH)[number]['value']>('orders_with_flight');
  // On phones the 11 filters would push every KPI below the first screen, so they start collapsed.
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useMemo(() => parseBoardFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<BoardFilters>) =>
    navigate({
      pathname: location.pathname,
      search: writeBoardFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
    });
  const request = useMemo(() => boardRequest(filters), [filters]);
  const query = useGetReportingLeLensData(LENS_ID, request);
  const cards = query.data?.cards ?? [];
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);
  const problemOf = (id: string) => {
    const result = card(id);
    return isProblem(result) ? (query.isFetching ? 'Loading…' : result.problem) : null;
  };
  const rowsFor = (id: string) => {
    const result = card(id);
    return isProblem(result) ? [] : result.rows;
  };
  const truncatedFor = (id: string) => {
    const result = card(id);
    return !isProblem(result) && result.truncated;
  };

  const kpiCard = cards.find((item) => item.cardId === 'd_kpis');
  const countCard = cards.find((item) => item.cardId === 'd_kpi_counts');
  const periodLabel = kpiCard?.period
    ? `${kpiCard.period.current.from} to ${kpiCard.period.current.to}${
        kpiCard.period.comparison ? ` vs ${kpiCard.period.comparison.from} to ${kpiCard.period.comparison.to}` : ''
      }`
    : '';
  const current = totalsOf({ ...kpiCard?.rows?.[0], ...countCard?.rows?.[0] });
  const previous = totalsOf({ ...kpiCard?.comparison?.rows?.[0], ...countCard?.comparison?.rows?.[0] });
  const rates = ratesOf(current);
  const previousRates = ratesOf(previous);
  const audShare = (() => {
    const currencies = rowsFor('s_currency');
    const aud = currencies.find((row) => text(row.sales_currency) === 'AUD');
    const all = currencies.reduce<number | null>((sum, row) => {
      const amount = num(row.gross_sales_aud);
      return amount === null ? sum : (sum ?? 0) + amount;
    }, null);
    return ratio(num(aud?.gross_sales_aud), all);
  })();
  const pct = (value: number | null) => (value === null ? 'vs prior: —' : `vs prior: ${(value * 100).toFixed(1)}%`);
  const tiles: Array<{ label: string; value: number | null; format: ValueFormat; note?: string }> = [
    {
      label: 'Gross sales',
      value: current.grossSales,
      format: 'currency',
      note: pct(growth(current.grossSales, previous.grossSales)),
    },
    {
      label: 'Net sales',
      value: current.netSales,
      format: 'currency',
      note: pct(growth(current.netSales, previous.netSales)),
    },
    {
      label: 'Gross margin (AR)',
      value: current.margin,
      format: 'currency',
      note: pct(growth(current.margin, previous.margin)),
    },
    {
      label: 'Average order value',
      value: rates.averageOrderValue,
      format: 'currency',
      note: pct(growth(rates.averageOrderValue, previousRates.averageOrderValue)),
    },
    {
      label: 'Unique orders',
      value: current.orders,
      format: 'count',
      note: pct(growth(current.orders, previous.orders)),
    },
    {
      label: 'Unique customers',
      value: current.customers,
      format: 'count',
      note: pct(growth(current.customers, previous.customers)),
    },
    { label: 'Items per basket', value: rates.itemsPerBasket, format: 'decimal' },
    { label: 'Percentage sales AUD', value: audShare, format: 'percent' },
    {
      label: 'Lead time (days)',
      value: rates.leadtime,
      format: 'decimal',
      note: pct(growth(rates.leadtime, previousRates.leadtime)),
    },
    {
      label: 'Refunds',
      value: current.refunds,
      format: 'currency',
      note: pct(growth(current.refunds, previous.refunds)),
    },
  ];

  const dateRows = (id: string, grain: Grain, columns: Array<PivotColumn>) =>
    rowsOf(rowsFor(id), (row) => bucketLabel(row[DATE_KEY], grain), columns);
  const dailyColumns = [...SUM_COLUMNS, ...PAY_COLUMNS];
  const dailyRows = dateRows('d_daily', 'day', dailyColumns);
  const dailyCounts = new Map(rowsFor('d_daily_orders').map((row) => [bucketLabel(row[DATE_KEY], 'day'), row]));
  const dailyWithOrders = dailyRows.map((row) => ({
    ...row,
    values: {
      ...row.values,
      orders: num(dailyCounts.get(row.label)?.orders),
      customers: num(dailyCounts.get(row.label)?.customers),
    },
  }));
  const productRows = rowsOf(rowsFor('d_product'), (row) => text(row.product_category), SUM_COLUMNS).sort(
    (a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0),
  );

  const monthlyCounts = new Map(rowsFor('m_monthly_counts').map((row) => [text(row[DATE_KEY]), row]));
  const monthly = rowsFor('m_monthly').map((row) => {
    const counts = monthlyCounts.get(text(row[DATE_KEY]));
    const totals = totalsOf({ ...row, ...counts });
    const monthRates = ratesOf(totals);
    return {
      key: text(row[DATE_KEY]),
      label: bucketLabel(row[DATE_KEY], 'month'),
      totals,
      rates: monthRates,
    };
  });
  const netGrowth = sequentialGrowth(monthly.map((month) => ({ key: month.key, value: month.totals.netSales })));
  const orderGrowth = sequentialGrowth(monthly.map((month) => ({ key: month.key, value: month.totals.orders })));
  const monthlyColumns: Array<PivotColumn> = [
    ...SUM_COLUMNS,
    { key: 'orders', label: 'Orders', format: 'count' },
    { key: 'customers', label: 'Customers', format: 'count' },
    { key: 'aov', label: 'AOV', format: 'currency' },
    { key: 'leadtime', label: 'Lead time (days)', format: 'decimal' },
    { key: 'netGrowth', label: 'Net sales MoM', format: 'percent' },
    { key: 'orderGrowth', label: 'Orders MoM', format: 'percent' },
  ];
  const monthlyRows: Array<PivotRow> = monthly.map((month, index) => ({
    label: month.label,
    values: {
      gross_sales_aud: month.totals.grossSales,
      refunds_aud: month.totals.refunds,
      net_sales_aud: month.totals.netSales,
      net_margin_aud: month.totals.margin,
      orders: month.totals.orders,
      customers: month.totals.customers,
      aov: month.rates.averageOrderValue,
      leadtime: month.rates.leadtime,
      netGrowth: netGrowth[index].growth,
      orderGrowth: orderGrowth[index].growth,
    },
  }));

  const tables: Record<string, Crosstab> = {
    vertical: crosstab(card('v_vertical'), 'product_category', 'month', 'currency', { measure: 'net_sales_aud' }),
    verticalOrders: crosstab(
      card('v_vertical_orders'),
      'product_category',
      'month',
      'count',
      { measure: 'orders' },
      false,
    ),
    region: crosstab(card('r_region'), 'deal_region', 'month', 'currency', { measure: 'net_sales_aud' }),
    items: crosstab(card('i_region'), 'deal_region', 'month', 'count', { measure: 'items' }, false),
    itemsPerBasket: crosstab(
      card('i_region'),
      'deal_region',
      'month',
      'decimal',
      { top: 'items', base: 'orders' },
      false,
    ),
    attachCategory: crosstab(
      card('a_category'),
      'product_category',
      'month',
      'percent',
      { top: attach, base: 'orders' },
      false,
    ),
    attachRegion: crosstab(card('a_region'), 'deal_region', 'month', 'percent', { top: attach, base: 'orders' }, false),
    supply: crosstab(card('y_supply'), 'offer_country', 'year', 'currency', { measure: 'net_sales_aud' }),
    sale: crosstab(card('y_sale'), 'customer_country', 'year', 'currency', { measure: 'net_sales_aud' }),
    refunds: crosstab(card('q_refunds'), 'deal_region', 'quarter', 'currency', { measure: 'refunds_aud' }),
    refundOrders: crosstab(
      card('q_refund_orders'),
      'deal_region',
      'quarter',
      'count',
      { measure: 'refunded_orders' },
      false,
    ),
  };
  const regionShare = shareOfColumn(tables.region);
  const currencyRows = rowsOf(rowsFor('s_currency'), (row) => text(row.sales_currency), SUM_COLUMNS).sort(
    (a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0),
  );
  const dealColumns = SUM_COLUMNS.filter((column) => column.key !== 'net_margin_aud');
  const dealRows = rowsOf(
    rowsFor('s_deals'),
    (row) => `${text(row.deal_name)} · ${text(row.supplier)}`,
    dealColumns,
  ).sort((a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0));

  const crosstabTable = (title: string, table: Crosstab, rowLabel: string, cardId: string, subtitle?: string) => (
    <PivotTable
      title={title}
      subtitle={subtitle}
      rowLabel={rowLabel}
      columns={table.columns}
      rows={table.rows}
      total={table.total}
      problem={problemOf(cardId)}
      truncated={truncatedFor(cardId)}
      csvName={`daily-board-${cardId}.csv`}
    />
  );

  const views: Record<BoardView, { sections: Array<PageSection>; body: React.ReactNode }> = {
    daily: {
      sections: [
        { title: 'Daily View', rows: dailyWithOrders, truncated: false },
        { title: 'Net Sales by product category', rows: productRows, truncated: false },
      ],
      body: (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <PivotTable
              title="Daily View"
              subtitle={periodLabel}
              rowLabel="Day"
              columns={[...dailyColumns, { key: 'orders', label: 'Orders', format: 'count' }]}
              rows={dailyWithOrders}
              total={totalOf(dailyRows, dailyColumns)}
              problem={problemOf('d_daily')}
              csvName="daily-board-daily.csv"
              nowrapRowLabel
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <PivotTable
              title="Net Sales"
              subtitle="By product category"
              rowLabel="Product category"
              columns={SUM_COLUMNS}
              rows={productRows}
              total={totalOf(productRows, SUM_COLUMNS)}
              problem={problemOf('d_product')}
              csvName="daily-board-net-sales.csv"
            />
          </Grid>
        </Grid>
      ),
    },
    monthly: {
      sections: [{ title: 'Monthly View', rows: monthlyRows, truncated: false }],
      body: (
        <PivotTable
          title="Monthly View and Month-over-Month Growth"
          subtitle="Last 13 months to yesterday; the latest month is partial."
          rowLabel="Month"
          columns={monthlyColumns}
          rows={monthlyRows}
          problem={problemOf('m_monthly') ?? problemOf('m_monthly_counts')}
          csvName="daily-board-monthly.csv"
        />
      ),
    },
    vertical: {
      sections: [
        { title: 'Net sales by product category', rows: tables.vertical.rows, truncated: false },
        { title: 'Orders by product category', rows: tables.verticalOrders.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          {crosstabTable(
            'Net sales by product category',
            tables.vertical,
            'Product category',
            'v_vertical',
            'Net sales by month',
          )}
          {crosstabTable(
            'Orders by product category',
            tables.verticalOrders,
            'Product category',
            'v_vertical_orders',
            'Orders by month',
          )}
        </Stack>
      ),
    },
    region: {
      sections: [
        { title: 'Net sales by deal region', rows: tables.region.rows, truncated: false },
        { title: 'Share of net sales by deal region', rows: regionShare.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          {crosstabTable('Net sales by deal region', tables.region, 'Deal region', 'r_region', 'Net sales by month')}
          {crosstabTable(
            'Share of net sales by deal region',
            regionShare,
            'Deal region',
            'r_region',
            'Share of each month’s net sales',
          )}
        </Stack>
      ),
    },
    items: {
      sections: [{ title: 'Items sold by deal region', rows: tables.items.rows, truncated: false }],
      body: (
        <Stack spacing={2}>
          {crosstabTable(
            'Items sold by deal region',
            tables.items,
            'Deal region',
            'i_region',
            'Distinct items sold by month',
          )}
          {crosstabTable(
            'Items per basket',
            tables.itemsPerBasket,
            'Deal region',
            'i_region',
            'Distinct items ÷ distinct orders',
          )}
        </Stack>
      ),
    },
    attach: {
      sections: [
        { title: `Attach rates by product category (${attach})`, rows: tables.attachCategory.rows, truncated: false },
        { title: `Attach rates by deal region (${attach})`, rows: tables.attachRegion.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={attach}
            onChange={(_, value) => value && setAttach(value)}
            aria-label="Attached product"
          >
            {ATTACH.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {crosstabTable(
            'Attach rates by product category',
            tables.attachCategory,
            'Product category',
            'a_category',
            'Orders including the product ÷ orders',
          )}
          {crosstabTable(
            'Attach rates by deal region',
            tables.attachRegion,
            'Deal region',
            'a_region',
            'Orders including the product ÷ orders',
          )}
        </Stack>
      ),
    },
    supplier: {
      sections: [
        { title: 'Net Sales by Currency', rows: currencyRows, truncated: false },
        { title: 'Sales by Deals', rows: dealRows, truncated: truncatedFor('s_deals') },
      ],
      body: (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, xl: 5 }}>
            <PivotTable
              title="Net Sales by Currency"
              subtitle={periodLabel}
              rowLabel="Sales currency"
              columns={SUM_COLUMNS}
              rows={currencyRows}
              total={totalOf(currencyRows, SUM_COLUMNS)}
              problem={problemOf('s_currency')}
              csvName="daily-board-currency.csv"
            />
          </Grid>
          <Grid size={{ xs: 12, xl: 7 }}>
            <PivotTable
              title="Sales by Deals"
              subtitle="Largest 2,000 deal and supplier pairs by net sales"
              rowLabel="Deal · Supplier"
              columns={dealColumns}
              rows={dealRows}
              problem={problemOf('s_deals')}
              csvName="daily-board-deals.csv"
            />
          </Grid>
        </Grid>
      ),
    },
    supply: {
      sections: [{ title: 'Net sales by point of supply', rows: tables.supply.rows, truncated: false }],
      body: crosstabTable(
        'Net sales by point of supply',
        tables.supply,
        'Offer country',
        'y_supply',
        'Net sales by calendar year',
      ),
    },
    sale: {
      sections: [{ title: 'Net sales by point of sale', rows: tables.sale.rows, truncated: false }],
      body: crosstabTable(
        'Net sales by point of sale',
        tables.sale,
        'Customer country',
        'y_sale',
        'Net sales by calendar year',
      ),
    },
    refunds: {
      sections: [
        { title: 'Refunds by deal region', rows: tables.refunds.rows, truncated: false },
        { title: 'Orders refunded by deal region', rows: tables.refundOrders.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          {crosstabTable(
            'Refunds by deal region',
            tables.refunds,
            'Deal region',
            'q_refunds',
            'Accounting refunds by quarter',
          )}
          {crosstabTable(
            'Orders refunded by deal region',
            tables.refundOrders,
            'Deal region',
            'q_refund_orders',
            'Distinct orders refunded by quarter',
          )}
        </Stack>
      ),
    },
  };

  const pageView = dailyBoardPageView({
    ready,
    filters,
    options,
    period: periodLabel,
    summary: Object.fromEntries(tiles.map((tile) => [tile.label, tile.value])),
    sections: views[filters.view].sections,
  });
  usePageBridge({
    pageId: DAILY_BOARD_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(boardEditFromBridge(changes, pageView, filters));
    },
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5">Daily Board</Typography>
            <Typography variant="body2" color="text.secondary">
              {periodLabel ? `Selected period: ${periodLabel}.` : 'Loading…'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <PageChatButton />
            <Button onClick={() => update(resetBoardFilters(filters))}>Reset filters</Button>
          </Stack>
        </Stack>
        {isPhone && (
          <Button
            variant="text"
            onClick={() => setFiltersOpen((open) => !open)}
            endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={filtersOpen}
            aria-controls="daily-board-filters"
            sx={{ alignSelf: 'flex-start' }}
          >
            {filtersOpen ? 'Hide filters' : 'Filters'}
          </Button>
        )}
        <Collapse in={!isPhone || filtersOpen} id="daily-board-filters">
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="daily-board-period">Sales period</InputLabel>
              <Select
                labelId="daily-board-period"
                label="Sales period"
                value={filters.salesPeriod}
                onChange={(event) => update({ salesPeriod: event.target.value })}
              >
                {SALES_PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="daily-board-year">Calendar Year or FY</InputLabel>
              <Select
                labelId="daily-board-year"
                label="Calendar Year or FY"
                value={filters.yearType}
                onChange={(event) => update({ yearType: event.target.value })}
              >
                {YEAR_TYPES.map((yearType) => (
                  <MenuItem key={yearType} value={yearType}>
                    {yearType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="daily-board-category">Product category</InputLabel>
              <Select
                labelId="daily-board-category"
                label="Product category"
                value={filters.productCategory}
                onChange={(event) => update({ productCategory: event.target.value })}
              >
                {PRODUCT_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.includeFlights}
                  onChange={(event) => update({ includeFlights: event.target.checked })}
                />
              }
              label="Include flights"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.removeAddonMigration}
                  onChange={(event) => update({ removeAddonMigration: event.target.checked })}
                />
              }
              label="Remove addon migration"
            />
            {BOARD_SELECTS.map(({ key, label }) => (
              <FilterSelect
                key={key}
                label={label}
                options={options[key] ?? []}
                value={filters.selections[key]}
                onChange={(value) => update({ selections: { ...filters.selections, [key]: value } })}
              />
            ))}
          </Stack>
        </Collapse>
        {failure && <Alert severity="error">{failure.message ?? 'The board could not load.'}</Alert>}
        <Grid container spacing={1.5}>
          {tiles.map((tile) => (
            <Grid key={tile.label} size={{ xs: 6, md: 12 / 5 }}>
              <KpiTile
                label={tile.label}
                value={tile.value}
                format={tile.format}
                note={tile.note}
                loading={!ready && !kpiCard}
              />
            </Grid>
          ))}
        </Grid>
        <Tabs
          value={filters.view}
          onChange={(_, value: BoardView) => update({ view: value })}
          variant="scrollable"
          scrollButtons="auto"
        >
          {BOARD_VIEWS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
        {views[filters.view].body}
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
          Source: {SOURCE}, rebuilt from the workbook’s legacy payment-type table. Monthly views cover 13 months, yearly
          views 3 calendar years and refunds 5 quarters, all to yesterday; the period selector drives the tiles, Daily
          View and Net Sales by Supplier. Growth compares with the previous window of the same length.
        </Typography>
      </Stack>
    </Container>
  );
}

export default function DailyBoardPage() {
  return (
    <MUIProvider>
      <PageChatLayout>
        <DailyBoardContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
